/**
 * Las marcas de la clínica, como las da el servidor (GET /api/marcas).
 *
 * Viven en la base (Configuración → Marcas): aquí no hay ninguna escrita. La
 * pantalla las pide UNA vez (se comparten entre todos los que las usan, con
 * una caché corta) y las pinta con su nombre y su color. Sin marcas, no hay
 * pestañas de marca y todo sale «Sin identificar».
 */
import { useEffect, useState } from 'react';
import { api } from './api';

export const SIN_IDENTIFICAR = 'Sin identificar';

const TTL_MS = 60 * 1000;
let cache = { lista: null, at: 0, promesa: null };
const oyentes = new Set();

function publicar(lista) {
  cache.lista = lista;
  cache.at = Date.now();
  oyentes.forEach((f) => f(lista));
}

/** Pide las marcas al servidor (o da las que ya tiene si son recientes). */
export function cargarMarcas(forzar = false) {
  if (!forzar && cache.lista && Date.now() - cache.at < TTL_MS) return Promise.resolve(cache.lista);
  if (cache.promesa && !forzar) return cache.promesa;
  const promesa = Promise.resolve()
    .then(() => api.get('/api/marcas'))
    .then((r) => {
      const lista = Array.isArray(r?.data?.marcas) ? r.data.marcas : [];
      publicar(lista);
      return lista;
    })
    .catch(() => {
      const lista = cache.lista || [];
      publicar(lista);
      return lista;
    })
    .finally(() => { if (cache.promesa === promesa) cache.promesa = null; });
  cache.promesa = promesa;
  return promesa;
}

/** Tras cambiarlas en Configuración (y en las pruebas): la próxima vez, al servidor. */
export function olvidarMarcas() {
  cache = { lista: null, at: 0, promesa: null };
}

/** Las marcas de hoy, en su orden. Vacío mientras llegan. */
export function useMarcas() {
  const [lista, setLista] = useState(cache.lista || []);
  useEffect(() => {
    let vivo = true;
    const avisa = (l) => { if (vivo) setLista(l); };
    oyentes.add(avisa);
    cargarMarcas().then(avisa);
    return () => { vivo = false; oyentes.delete(avisa); };
  }, []);
  return lista;
}

export function marcaDe(lista, clave) {
  return (lista || []).find((m) => m.clave === clave) || null;
}

export function nombreMarca(lista, clave) {
  return marcaDe(lista, clave)?.nombre || SIN_IDENTIFICAR;
}

/** «de la Clínica Alfa»; sin él, «de <nombre>». */
export function delMarca(lista, clave) {
  const m = marcaDe(lista, clave);
  return m ? (m.del || `de ${m.nombre}`) : '';
}

const HEX = /^#[0-9a-fA-F]{6}$/;

/** «#1D4ED8» con transparencia → «rgba(29, 78, 216, 0.1)». */
function conAlfa(hex, alfa) {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alfa})`;
}

/**
 * Cómo se pinta la etiqueta de una marca: {chip, punto} en estilos en línea
 * (los colores vienen de la base, no de clases escritas aquí). Con
 * `color_texto`, en sólido (fondo del color, letra de ese otro); si no, el
 * fondo claro del color y la letra y el borde de él.
 */
export function estiloMarca(m) {
  const color = HEX.test(m?.color || '') ? m.color : '#475569';
  const punto = HEX.test(m?.color_punto || '') ? m.color_punto : null;
  if (HEX.test(m?.color_texto || '')) {
    return {
      chip: { backgroundColor: color, color: m.color_texto, borderColor: color },
      punto: { backgroundColor: punto || m.color_texto },
    };
  }
  return {
    chip: { backgroundColor: conAlfa(color, 0.1), color, borderColor: conAlfa(color, 0.5) },
    punto: { backgroundColor: punto || color },
  };
}

/** Las iniciales del monograma: las suyas o las de su nombre. */
export function inicialesMarca(m) {
  if (m?.nombre_corto) return m.nombre_corto;
  return String(m?.nombre || '').split(/\s+/).filter(Boolean).slice(0, 3)
    .map((p) => p[0]).join('').toUpperCase();
}
